import React, { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowRight,
  Check,
  CheckCircle2,
  Copy,
  ExternalLink,
  FileCheck,
  HelpCircle,
  Lock,
  Printer,
  Search,
  ShieldAlert,
  ShieldCheck,
  Sparkles,
  Trash2,
  XCircle,
} from "lucide-react";
import { API_BASE } from "../config";
import { formatBytes } from "../util";

interface CertificateData {
  transferId: string;
  signature: string;
  deletedAt: number;
  fileCount: number;
  totalBytes: number;
}

export function ReceiptVerify() {
  const { id: paramId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  // URL query params
  const initialId = paramId || searchParams.get("id") || "";
  const initialSig = searchParams.get("sig") || "";
  const initialTime = searchParams.get("time") || "";
  const initialFiles = searchParams.get("files") || "";
  const initialBytes = searchParams.get("bytes") || "";

  const [inputCode, setInputCode] = useState("");
  const [certData, setCertData] = useState<CertificateData | null>(
    initialId && initialSig
      ? {
          transferId: initialId,
          signature: initialSig,
          deletedAt: Number(initialTime) || Date.now(),
          fileCount: Number(initialFiles) || 1,
          totalBytes: Number(initialBytes) || 0,
        }
      : null,
  );

  const [verifiedResult, setVerifiedResult] = useState<{
    valid: boolean;
    transferId: string;
    deletedAt: number;
    fileCount: number;
    totalBytes: number;
  } | null>(null);

  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // Manual advanced inputs
  const [advId, setAdvId] = useState(initialId);
  const [advSig, setAdvSig] = useState(initialSig);
  const [advTime, setAdvTime] = useState(initialTime);
  const [advFiles, setAdvFiles] = useState(initialFiles);
  const [advBytes, setAdvBytes] = useState(initialBytes);

  const verifyCertificate = async (data: CertificateData) => {
    setLoading(true);
    try {
      const q = new URLSearchParams({
        sig: data.signature,
        time: String(data.deletedAt),
        files: String(data.fileCount),
        bytes: String(data.totalBytes),
      });
      const res = await fetch(`${API_BASE}/v1/transfers/${encodeURIComponent(data.transferId)}/receipt?${q.toString()}`);
      if (!res.ok) throw new Error("Verification request failed");
      const result = await res.json();
      setVerifiedResult(result);
    } catch {
      setVerifiedResult({
        valid: false,
        transferId: data.transferId,
        deletedAt: data.deletedAt,
        fileCount: data.fileCount,
        totalBytes: data.totalBytes,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (certData?.transferId && certData?.signature) {
      void verifyCertificate(certData);
    }
  }, [certData?.transferId, certData?.signature]);

  // Handle smart paste: user pastes JSON or link or code
  const handleSmartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const raw = inputCode.trim();
    if (!raw) return;

    // Check if pasted a URL like: https://limeshare.org/verify-receipt?id=...&sig=...
    if (raw.includes("sig=") && raw.includes("id=")) {
      try {
        const url = new URL(raw.startsWith("http") ? raw : `https://${raw}`);
        const id = url.searchParams.get("id") || "";
        const sig = url.searchParams.get("sig") || "";
        const time = Number(url.searchParams.get("time")) || Date.now();
        const files = Number(url.searchParams.get("files")) || 1;
        const bytes = Number(url.searchParams.get("bytes")) || 0;
        if (id && sig) {
          setCertData({ transferId: id, signature: sig, deletedAt: time, fileCount: files, totalBytes: bytes });
          return;
        }
      } catch {
        /* fallback */
      }
    }

    // Check if pasted JSON
    if (raw.startsWith("{") && raw.endsWith("}")) {
      try {
        const parsed = JSON.parse(raw);
        if (parsed.transferId && (parsed.signature || parsed.receipt?.signature)) {
          const r = parsed.receipt || parsed;
          setCertData({
            transferId: r.transferId,
            signature: r.signature,
            deletedAt: r.deletedAt || Date.now(),
            fileCount: r.fileCount || 1,
            totalBytes: r.totalBytes || 0,
          });
          return;
        }
      } catch {
        /* fallback */
      }
    }

    alert("Could not parse certificate. Please paste a valid Certificate link or JSON.");
  };

  // Demo certificate generator so any visitor immediately sees how it works
  const loadDemoCertificate = () => {
    const demo: CertificateData = {
      transferId: "sample_demo_vault",
      signature: "demo_sig_f8e29a3c107b4d_verified",
      deletedAt: Date.now() - 3600000,
      fileCount: 3,
      totalBytes: 734003200,
    };
    setVerifiedResult({
      valid: true,
      transferId: demo.transferId,
      deletedAt: demo.deletedAt,
      fileCount: demo.fileCount,
      totalBytes: demo.totalBytes,
    });
    setCertData(demo);
  };

  const copyVerificationLink = async () => {
    if (!certData) return;
    const url = `${window.location.origin}/verify-receipt?id=${encodeURIComponent(certData.transferId)}&sig=${encodeURIComponent(certData.signature)}&time=${certData.deletedAt}&files=${certData.fileCount}&bytes=${certData.totalBytes}`;
    await navigator.clipboard.writeText(url);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <article className="panel legal" style={{ maxWidth: 820, margin: "0 auto", padding: "32px 28px" }}>
      {/* Header */}
      <header style={{ marginBottom: 28 }}>
        <div style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 12px", background: "rgba(0, 255, 102, 0.1)", borderRadius: 999, border: "1px solid rgba(0, 255, 102, 0.25)", fontSize: 12, color: "#7cff5c", fontFamily: "ui-monospace, monospace", marginBottom: 14 }}>
          <ShieldCheck size={14} /> Legal &amp; Compliance Verification
        </div>
        <h1 style={{ fontSize: "clamp(26px, 4vw, 36px)", lineHeight: 1.15, margin: "0 0 14px", fontWeight: 800 }}>
          Certified Destruction Verifier
        </h1>
        <p className="hint" style={{ fontSize: 15, lineHeight: 1.6, margin: 0 }}>
          Cryptographic proof that your files were permanently erased from cloud infrastructure.
          Every destroyed transfer issues an immutable signed certificate for legal, NDA, and compliance records.
        </p>
      </header>

      {/* Explainer / Where do I get it? Callout */}
      <div style={{ padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius-sm)", marginBottom: 28, display: "flex", gap: 14, alignItems: "flex-start" }}>
        <HelpCircle size={22} color="var(--lime)" style={{ flexShrink: 0, marginTop: 2 }} />
        <div style={{ fontSize: 13, lineHeight: 1.55 }}>
          <b style={{ color: "var(--text)", display: "block", marginBottom: 4 }}>How does Certified Destruction work?</b>
          <span style={{ color: "var(--muted)" }}>
            When you send files on LimeShare and click <b>Destroy</b> (or when files expire after 48h / burn after download),
            our servers erase all binary chunks and sign an official certificate with the exact timestamp and byte count.
            Holders of this certificate can verify it here at any time.
          </span>
          <div style={{ marginTop: 10 }}>
            <button className="btn ghost btn-sm" onClick={loadDemoCertificate}>
              <Sparkles size={13} color="var(--lime)" /> View Interactive Sample Certificate
            </button>
          </div>
        </div>
      </div>

      {/* Verified Certificate Display (Formal Enterprise Document) */}
      {verifiedResult && (
        <section
          style={{
            padding: 28,
            background: verifiedResult.valid ? "rgba(6, 24, 10, 0.9)" : "rgba(30, 10, 10, 0.9)",
            border: `2px solid ${verifiedResult.valid ? "#00ff66" : "#ff5c5c"}`,
            borderRadius: "var(--radius)",
            marginBottom: 32,
            boxShadow: verifiedResult.valid ? "0 0 40px rgba(0, 255, 102, 0.15)" : "0 0 40px rgba(255, 92, 92, 0.15)",
          }}
        >
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 14, marginBottom: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {verifiedResult.valid ? <CheckCircle2 size={36} color="#00ff66" /> : <XCircle size={36} color="#ff5c5c" />}
              <div>
                <span style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: "0.1em", color: verifiedResult.valid ? "#00ff66" : "#ff5c5c", fontWeight: 700 }}>
                  {verifiedResult.valid ? "Official Proof of Erasure" : "Invalid Certificate"}
                </span>
                <h2 style={{ margin: "2px 0 0", fontSize: 22, color: "#fff" }}>
                  {verifiedResult.valid ? "Certificate of Data Destruction" : "Verification Failed"}
                </h2>
              </div>
            </div>

            {verifiedResult.valid && (
              <div style={{ display: "flex", gap: 8 }}>
                <button className="btn ghost btn-sm" onClick={copyVerificationLink}>
                  {copied ? <Check size={14} color="#00ff66" /> : <Copy size={14} />}
                  <span>{copied ? "Link Copied" : "Copy Verification URL"}</span>
                </button>
                <button className="btn ghost btn-sm" onClick={() => window.print()}>
                  <Printer size={14} /> Print
                </button>
              </div>
            )}
          </div>

          {verifiedResult.valid ? (
            <>
              <div style={{ padding: "12px 16px", background: "rgba(0, 255, 102, 0.08)", border: "1px solid rgba(0, 255, 102, 0.2)", borderRadius: 8, fontSize: 13, marginBottom: 18, color: "#c8f5ce" }}>
                This document certifies that the data objects associated with Transfer ID <code>{verifiedResult.transferId}</code> have been
                permanently purged from cloud storage. Zero unencrypted residual bytes remain.
              </div>

              <div className="kv" style={{ marginTop: 0 }}>
                <span>Transfer ID</span>
                <b style={{ fontFamily: "ui-monospace, monospace" }}>{verifiedResult.transferId}</b>
                <span>Destruction Date &amp; Time (UTC)</span>
                <b>{new Date(verifiedResult.deletedAt).toUTCString()}</b>
                <span>Destruction Date &amp; Time (Local)</span>
                <b>{new Date(verifiedResult.deletedAt).toLocaleString()}</b>
                <span>Objects Erased</span>
                <b>{verifiedResult.fileCount} file(s)</b>
                <span>Total Data Purged</span>
                <b>{formatBytes(verifiedResult.totalBytes)}</b>
                <span>Erasure Mechanism</span>
                <b style={{ color: "var(--lime)" }}>Zero-Residual Cryptographic Purge</b>
                <span>Cryptographic Seal</span>
                <b style={{ color: "var(--lime)", fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                  {certData?.signature.slice(0, 28)}… [VALID ED25519/HMAC SIGNATURE]
                </b>
              </div>
            </>
          ) : (
            <div style={{ color: "#ffb4b4", fontSize: 14 }}>
              The provided signature or parameters do not match our key infrastructure records. This certificate may be forged, modified, or expired.
            </div>
          )}
        </section>
      )}

      {/* 1-Box Smart Search */}
      <section style={{ marginBottom: 28 }}>
        <h3 style={{ fontSize: 16, margin: "0 0 10px" }}>Verify another Certificate</h3>
        <form onSubmit={handleSmartSubmit}>
          <div style={{ display: "flex", gap: 10 }}>
            <input
              type="text"
              placeholder="Paste verification link or Certificate JSON here…"
              value={inputCode}
              onChange={(e) => setInputCode(e.target.value)}
              className="textinput"
              style={{ marginTop: 0 }}
            />
            <button className="btn" type="submit" style={{ flexShrink: 0 }}>
              <Search size={16} /> Verify
            </button>
          </div>
        </form>
      </section>

      {/* Accordion: Advanced Raw Fields for Auditors */}
      <div style={{ marginTop: 16 }}>
        <button
          className="btn ghost btn-sm"
          onClick={() => setShowAdvanced(!showAdvanced)}
          style={{ fontSize: 12 }}
        >
          {showAdvanced ? "Hide advanced fields" : "Auditor mode: enter raw certificate fields…"}
        </button>

        {showAdvanced && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              setCertData({
                transferId: advId,
                signature: advSig,
                deletedAt: Number(advTime) || Date.now(),
                fileCount: Number(advFiles) || 1,
                totalBytes: Number(advBytes) || 0,
              });
            }}
            style={{ marginTop: 14, padding: 18, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: 10 }}
          >
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <div>
                <label style={{ fontSize: 12, color: "var(--muted)" }}>Transfer ID:</label>
                <input className="textinput" value={advId} onChange={(e) => setAdvId(e.target.value)} placeholder="e.g. 4iKqZmPB7Ub3dZCB" required />
              </div>
              <div>
                <label style={{ fontSize: 12, color: "var(--muted)" }}>Cryptographic Signature:</label>
                <input className="textinput" value={advSig} onChange={(e) => setAdvSig(e.target.value)} placeholder="Base64 signature" required />
              </div>
              <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--muted)" }}>Timestamp (ms):</label>
                  <input className="textinput" value={advTime} onChange={(e) => setAdvTime(e.target.value)} placeholder="e.g. 1758600000000" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--muted)" }}>File Count:</label>
                  <input className="textinput" value={advFiles} onChange={(e) => setAdvFiles(e.target.value)} placeholder="1" />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ fontSize: 12, color: "var(--muted)" }}>Total Bytes:</label>
                  <input className="textinput" value={advBytes} onChange={(e) => setAdvBytes(e.target.value)} placeholder="Total bytes" />
                </div>
              </div>
              <button className="btn" type="submit" disabled={loading} style={{ marginTop: 6 }}>
                {loading ? "Checking…" : "Validate Raw Fields"}
              </button>
            </div>
          </form>
        )}
      </div>

      {/* Legal & Compliance Explanations */}
      <section style={{ marginTop: 36, paddingTop: 28, borderTop: "1px solid var(--border)" }}>
        <h2>Why Certified Destruction Matters for Business</h2>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, marginTop: 16 }}>
          <div className="feature">
            <b>GDPR Article 17 (Right to Erasure)</b>
            <span>Provides verifiable, immutable proof that personal data was permanently expunged upon request.</span>
          </div>
          <div className="feature">
            <b>Legal Discovery &amp; NDAs</b>
            <span>Attorneys and clients can prove exact timelines when confidential settlement documents were destroyed.</span>
          </div>
          <div className="feature">
            <b>Financial &amp; Tax Audits</b>
            <span>Ensures tax records, W-2s, and accounting ledgers do not persist in unsecured cloud caches.</span>
          </div>
        </div>
      </section>

      <div style={{ padding: 22, background: "var(--panel-2)", border: "1px solid var(--border)", borderRadius: "var(--radius)", textAlign: "center", marginTop: 28 }}>
        <h3 style={{ margin: "0 0 8px" }}>Send files with guaranteed destruction</h3>
        <p className="hint" style={{ margin: "0 0 16px" }}>
          Every transfer on LimeShare includes free client-side encryption and certified erasure.
        </p>
        <Link to="/" className="btn" style={{ textDecoration: "none" }}>
          <span>Start Encrypted Transfer</span> <ArrowRight size={15} />
        </Link>
      </div>
    </article>
  );
}
